import storage from 'redux-persist/lib/storage';

import { api } from '~/redux/api/createApi';
import { esign } from '~/redux/api/esign';
import { evisearch } from '~/redux/api/evisearch';
import { hermes } from '~/redux/api/hermes';
import { k2 } from '~/redux/api/k2';
import { managedEntities } from '~/redux/api/managed-entities';
import { unifiedFields } from '~/redux/api/unified-fields';

// Applied only in local dev (see `store.ts`). Only the RTK Query API caches
// are persisted; plain UI/feature slices stay transient.
export const persistConfig = {
  key: 'evisort-root',
  version: 1,
  storage,
  whitelist: [
    api.reducerPath,
    k2.reducerPath,
    unifiedFields.reducerPath,
    esign.reducerPath,
    evisearch.reducerPath,
    managedEntities.reducerPath,
    hermes.reducerPath,
  ],
};

// ─── Opt-in preference (dev only) ─────────────────────────────────────────────

const PREFERENCE_STORAGE_KEY = 'evisort:dev:redux-persist-enabled';

// Where redux-persist writes the persisted state (`persist:` + config key).
const PERSIST_STORAGE_KEY = `persist:${persistConfig.key}`;

// Opt-in: persistence is off unless explicitly enabled. Read once at store
// init, so a change needs a reload to take effect.
export function isReduxPersistEnabled(): boolean {
  try {
    return localStorage.getItem(PREFERENCE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

const listeners = new Set<() => void>();
let enabledSnapshot = isReduxPersistEnabled();

// External store backing the devtools toggle, mirrored to localStorage.
export const reduxPersistPreference = {
  getSnapshot(): boolean {
    return enabledSnapshot;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  setEnabled(enabled: boolean): void {
    try {
      localStorage.setItem(PREFERENCE_STORAGE_KEY, String(enabled));
    } catch {
      // Ignore: a disabled/unavailable localStorage just means no persistence.
    }
    enabledSnapshot = enabled;
    listeners.forEach((listener) => listener());
  },
  /** Removes any persisted state from localStorage. */
  clearPersistedState(): void {
    try {
      localStorage.removeItem(PERSIST_STORAGE_KEY);
    } catch {
      // Ignore.
    }
  },
};
